/**
 * OfferingClassManagementPage
 *
 * Three-tab dashboard for managing a single published offering:
 *   Roster   — enrolled students, pending approvals, manual add
 *   Sessions — schedule sessions, view upcoming/past, navigate to detail
 *   Broadcast — send a message to all confirmed students
 *
 * The offering title is resolved from the hub cache so the header always
 * shows *which* class the teacher is managing, not just "Class Management".
 */

import * as React from "react";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle, ArrowLeft, BookOpen, CalendarPlus, CheckCircle2, ChevronDown, ChevronLeft,
  ChevronRight, Clock, Globe, Loader2, Mail, MapPin, Pencil, RefreshCw, Send, Trash2,
  UserCheck, UserMinus, UserPlus, Users, X,
} from "lucide-react";
import { SidebarProvider } from "@/components/ui/sidebar";
import { TeacherSidebar } from "@/components/teacher/dashboard/TeacherSidebar";
import { DashboardTopNav } from "@/components/layout/DashboardTopNav";
import { useTeacherProfile } from "@/contexts/TeacherProfileContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { useMyOfferings } from "@/hooks/useMyOfferings";
import {
  useClassManagementHub,
  useOfferingEnrollments,
  useConfirmEnrollment,
  useRemoveEnrollment,
  useAddEnrollmentByEmail,
  useOfferingSessions,
  useCreateSession,
  useCancelSession,
  useRescheduleSession,
  useSendBroadcast,
  useTeacherBroadcasts,
  useEditBroadcast,
  useDeleteBroadcast,
} from "@/hooks/use-class-management";
import { BroadcastComposer } from "@/components/teacher/classes/BroadcastComposer";
import { ScheduleWarningBanner } from "@/components/teacher/classes/ScheduleWarningBanner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
// Note: Dialog imports retained for AddStudentDialog
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { EnrollmentItem, ScheduleWarning } from "@/services/classManagementService";
import type { OfferingResponse } from "@/types/offering";
import { ASSESSMENT_BADGE } from "@/components/teacher/offering/MilestonesBuilder";

// ── Shared helpers ────────────────────────────────────────────────────────────

function StudentAvatar({ name }: { name: string | undefined }) {
  const initials = (name || "")
    .split(" ")
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
      <span className="text-xs font-bold text-indigo-700">{initials || "?"}</span>
    </div>
  );
}

function formatSessionDate(iso: string) {
  const d = new Date(iso);
  return {
    weekday: d.toLocaleDateString([], { weekday: "short" }),
    date:    d.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }),
    time:    d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    dayNum:  d.getDate(),
  };
}

// ── Roster Tab ────────────────────────────────────────────────────────────────

// ── Add Student Dialog ────────────────────────────────────────────────────────

function AddStudentDialog({
  offeringId,
  open,
  onClose,
}: {
  offeringId: string;
  open: boolean;
  onClose: () => void;
}) {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const addMutation = useAddEnrollmentByEmail();

  function handleClose() {
    setEmail("");
    setError("");
    addMutation.reset();
    onClose();
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError("");
    const trimmed = email.trim();
    if (!trimmed) { setError("Please enter a student email."); return; }

    try {
      await addMutation.mutateAsync({ studentEmail: trimmed, offeringId });
      toast({ title: `${trimmed} added as pending enrollment.` });
      handleClose();
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status;
      if (status === 404) setError("No account found for that email. Ask the student to sign up first.");
      else if (status === 409) setError("This student is already enrolled in this class.");
      else setError("Something went wrong. Please try again.");
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Student</DialogTitle>
        </DialogHeader>
        <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-4 pt-1">
          <div className="space-y-1.5">
            <label htmlFor="add-student-email" className="text-sm font-medium text-slate-700">
              Student email address
            </label>
            <Input
              id="add-student-email"
              type="email"
              required
              placeholder="student@example.com"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(""); }}
              autoFocus
              className="cursor-text"
            />
            <p className="text-xs text-slate-400">
              The student must already have a BookMyCoaching account.
            </p>
          </div>
          {error && <p role="alert" className="text-sm text-red-600 font-medium">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} className="cursor-pointer">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={addMutation.isPending}
              className="cursor-pointer bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              {addMutation.isPending && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />}
              Add as pending
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Roster Tab ────────────────────────────────────────────────────────────────

function RosterTab({ offeringId }: { offeringId: string }) {
  const { data, isLoading } = useOfferingEnrollments(offeringId);
  const confirmMutation = useConfirmEnrollment();
  const removeMutation  = useRemoveEnrollment();
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  const confirmed = data?.filter((e) => e.status === "CONFIRMED") ?? [];
  const pending   = data?.filter((e) => e.status === "PENDING")   ?? [];

  async function handleConfirm(enrollment: EnrollmentItem) {
    try {
      await confirmMutation.mutateAsync(enrollment.id);
      toast({ title: `${enrollment.studentName || "Student"} confirmed.` });
    } catch {
      toast({ title: "Failed to confirm enrollment.", variant: "destructive" });
    }
  }

  function handleRemove(enrollment: EnrollmentItem) {
    const name = enrollment.studentName || enrollment.studentEmail || "this student";
    if (!window.confirm(`Remove ${name} from this class?`)) return;
    removeMutation.mutate(enrollment.id, {
      onSuccess: () => toast({ title: `${name} removed.` }),
      onError:   () => toast({ title: "Failed to remove student.", variant: "destructive" }),
    });
  }

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex items-center gap-3 bg-white rounded-xl border border-slate-200 p-4 animate-pulse">
            <div className="w-8 h-8 rounded-full bg-slate-100 shrink-0" />
            <div className="flex-1 space-y-1.5">
              <div className="h-4 bg-slate-100 rounded w-1/3" />
              <div className="h-3 bg-slate-100 rounded w-1/4" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5 text-sm text-slate-600">
            <UserCheck className="w-4 h-4 text-emerald-500" aria-hidden="true" />
            <span className="font-semibold text-slate-800">{confirmed.length}</span>
            <span>confirmed</span>
          </div>
          {pending.length > 0 && (
            <div className="flex items-center gap-1.5 text-sm text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full">
              <Clock className="w-3.5 h-3.5" aria-hidden="true" />
              <span className="font-semibold">{pending.length}</span> pending approval
            </div>
          )}
        </div>
        <Button
          size="sm"
          className="cursor-pointer bg-indigo-600 hover:bg-indigo-700 text-white gap-2 transition-colors duration-150"
          onClick={() => setAddDialogOpen(true)}
        >
          <UserPlus className="w-4 h-4" aria-hidden="true" />
          Add student
        </Button>
      </div>

      <AddStudentDialog
        offeringId={offeringId}
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
      />

      {/* Pending approvals */}
      {pending.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide px-1">
            Pending approval
          </p>
          {pending.map((enrollment) => (
            <div
              key={enrollment.id}
              className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4"
            >
              <StudentAvatar name={enrollment.studentName || enrollment.studentEmail} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900 truncate">
                  {enrollment.studentName || "—"}
                </p>
                <p className="text-xs text-slate-500 truncate">{enrollment.studentEmail}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button
                  size="sm"
                  className="cursor-pointer bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5 transition-colors duration-150"
                  data-testid={`confirm-enrollment-${enrollment.id}`}
                  disabled={confirmMutation.isPending}
                  onClick={() => void handleConfirm(enrollment)}
                  aria-label={`Confirm ${enrollment.studentName || enrollment.studentEmail || "student"}`}
                >
                  <UserCheck className="w-3.5 h-3.5" aria-hidden="true" />
                  Confirm
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="cursor-pointer text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors duration-150 h-8 w-8 p-0"
                  disabled={removeMutation.isPending}
                  onClick={() => handleRemove(enrollment)}
                  aria-label={`Remove ${enrollment.studentName || enrollment.studentEmail || "student"}`}
                >
                  <UserMinus className="w-3.5 h-3.5" aria-hidden="true" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Confirmed students */}
      <div className="space-y-2">
        {pending.length > 0 && confirmed.length > 0 && (
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide px-1">
            Enrolled students
          </p>
        )}

        {confirmed.length === 0 && pending.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center py-14 bg-white rounded-2xl border border-slate-200">
            <Users className="w-8 h-8 text-slate-300 mb-3" aria-hidden="true" />
            <p className="text-sm font-semibold text-slate-700 mb-0.5">No students enrolled yet</p>
            <p className="text-xs text-slate-400 mb-4">Add a student by email or wait for them to join via inquiry.</p>
            <Button
              size="sm"
              variant="outline"
              className="cursor-pointer gap-2"
              onClick={() => setAddDialogOpen(true)}
            >
              <UserPlus className="w-3.5 h-3.5" aria-hidden="true" />
              Add first student
            </Button>
          </div>
        ) : confirmed.length === 0 ? null : (
          confirmed.map((enrollment) => (
            <div
              key={enrollment.id}
              className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-4 hover:border-slate-300 transition-colors duration-150"
            >
              <StudentAvatar name={enrollment.studentName || enrollment.studentEmail} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900 truncate">
                  {enrollment.studentName || "—"}
                </p>
                <p className="text-xs text-slate-500 truncate">{enrollment.studentEmail}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-50 text-xs">
                  Confirmed
                </Badge>
                <Button
                  size="sm"
                  variant="ghost"
                  className="cursor-pointer text-slate-300 hover:text-red-600 hover:bg-red-50 transition-colors duration-150 h-8 w-8 p-0"
                  disabled={removeMutation.isPending}
                  onClick={() => handleRemove(enrollment)}
                  aria-label={`Remove ${enrollment.studentName || enrollment.studentEmail}`}
                >
                  <UserMinus className="w-3.5 h-3.5" aria-hidden="true" />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ── Schedule Session Panel (inline) ──────────────────────────────────────────

const DURATION_OPTIONS = [30, 45, 60, 90, 120];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
const RECUR_ENDS = ["count", "date"] as const;

/** All IANA timezone names, sorted alphabetically. */
const ALL_TIMEZONES: string[] = (() => {
  try {
    return (Intl as unknown as { supportedValuesOf: (k: string) => string[] })
      .supportedValuesOf("timeZone")
      .sort();
  } catch {
    // Fallback subset for older browsers
    return [
      "Africa/Cairo", "America/Chicago", "America/Los_Angeles", "America/New_York",
      "America/Sao_Paulo", "Asia/Colombo", "Asia/Dubai", "Asia/Kolkata",
      "Asia/Seoul", "Asia/Singapore", "Asia/Tokyo", "Australia/Sydney",
      "Europe/Berlin", "Europe/London", "Europe/Moscow", "Europe/Paris",
      "Pacific/Auckland", "UTC",
    ];
  }
})();

/** User's local IANA timezone (e.g. "Asia/Kolkata"). */
const LOCAL_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Converts a datetime-local string (wall-clock time in `timeZone`) to a UTC ISO string.
 * e.g. "2026-10-14T10:00" + "Asia/Kolkata" → "2026-10-14T04:30:00.000Z"
 */
function zonedToUtcISO(datetimeLocal: string, timeZone: string): string {
  const [datePart, timePart] = datetimeLocal.split("T");
  const [year, month, day]   = datePart.split("-").map(Number);
  const [hour, minute]       = timePart.split(":").map(Number);

  // Approximate UTC by treating input as UTC, then correct for the actual offset.
  const approx = new Date(Date.UTC(year, month - 1, day, hour, minute));
  const parts: Record<string, string> = {};
  new Intl.DateTimeFormat("en-US", {
    timeZone, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(approx).forEach(({ type, value }) => {
    if (type !== "literal") parts[type] = value;
  });

  const h = parseInt(parts.hour === "24" ? "0" : parts.hour, 10);
  const gotMs = Date.UTC(
    parseInt(parts.year, 10), parseInt(parts.month, 10) - 1,
    parseInt(parts.day, 10), h, parseInt(parts.minute, 10),
  );
  return new Date(approx.getTime() + (Date.UTC(year, month - 1, day, hour, minute) - gotMs)).toISOString();
}

function durationLabel(m: number) {
  return m < 60 ? `${m} min` : `${m / 60}h${m % 60 ? ` ${m % 60}m` : ""}`;
}

/** Generates wall-clock datetime-local strings for a recurring pattern. */
function buildRecurringDates(
  start: string,       // datetime-local value, e.g. "2026-10-14T10:00"
  weekdays: number[],  // 0=Sun…6=Sat
  endsBy: "count" | "date",
  count: number,
  untilDate: string,   // "YYYY-MM-DD"
): string[] {
  if (!start || weekdays.length === 0) return [];
  const [datePart, timePart] = start.split("T");
  const [y, mo, d] = datePart.split("-").map(Number);
  const [h, mi]    = timePart.split(":").map(Number);

  const results: string[] = [];
  const until = endsBy === "date" && untilDate ? new Date(untilDate + "T23:59:59") : null;
  const max   = endsBy === "count" ? count : 52;
  const pad   = (n: number) => String(n).padStart(2, "0");

  for (let day = 0; results.length < max && day <= 365; day++) {
    const cur = new Date(y, mo - 1, d + day, h, mi, 0);
    if (until && cur > until) break;
    if (weekdays.includes(cur.getDay())) {
      results.push(
        `${cur.getFullYear()}-${pad(cur.getMonth() + 1)}-${pad(cur.getDate())}T${pad(h)}:${pad(mi)}`
      );
    }
  }
  return results;
}

function ScheduleSessionPanel({
  offeringId,
  onDone,
}: {
  offeringId: string;
  onDone: () => void;
}) {
  const createSession = useCreateSession();

  const [mode, setMode]         = useState<"single" | "recurring">("single");
  // Date/time stored as separate pieces for the custom picker
  const [pickedDate, setPickedDate] = useState<Date | undefined>(undefined);
  const [pickedHour, setPickedHour] = useState("09");
  const [pickedMin,  setPickedMin]  = useState("00");
  const [calOpen,    setCalOpen]    = useState(false);
  const [duration,   setDuration]   = useState(60);
  const [timezone,   setTimezone]   = useState(LOCAL_TIMEZONE);
  const [tzSearch,   setTzSearch]   = useState("");
  const [tzOpen,     setTzOpen]     = useState(false);
  const [error,      setError]      = useState("");
  const [scheduleWarning, setScheduleWarning] = useState<ScheduleWarning | null>(null);

  const [weekdays, setWeekdays]   = useState<number[]>([]);
  const [endsBy, setEndsBy]       = useState<"count" | "date">("count");
  const [count, setCount]         = useState(8);
  const [untilDate, setUntilDate] = useState<Date | undefined>(undefined);

  const [batchTotal,   setBatchTotal]   = useState(0);
  const [batchDone,    setBatchDone]    = useState(0);
  const [batchFailed,  setBatchFailed]  = useState(0);
  const [batchRunning, setBatchRunning] = useState(false);

  const tzRef = React.useRef<HTMLDivElement>(null);

  // Close timezone dropdown when clicking outside
  React.useEffect(() => {
    if (!tzOpen) return;
    function onClick(e: MouseEvent) {
      if (tzRef.current && !tzRef.current.contains(e.target as Node)) setTzOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [tzOpen]);

  const filteredTz = tzSearch.trim()
    ? ALL_TIMEZONES.filter((tz) => tz.toLowerCase().includes(tzSearch.toLowerCase()))
    : ALL_TIMEZONES;

  // Build "YYYY-MM-DDTHH:mm" string from picked pieces
  const pad = (n: string | number) => String(n).padStart(2, "0");
  function toDatetimeLocal(date: Date | undefined, h: string, m: string): string {
    if (!date) return "";
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(h)}:${pad(m)}`;
  }
  const startDateLocal = toDatetimeLocal(pickedDate, pickedHour, pickedMin);

  // Until date as "YYYY-MM-DD" string
  const untilDateStr = untilDate
    ? `${untilDate.getFullYear()}-${pad(untilDate.getMonth() + 1)}-${pad(untilDate.getDate())}`
    : "";

  function reset() {
    setMode("single");
    setPickedDate(undefined); setPickedHour("09"); setPickedMin("00");
    setCalOpen(false); setDuration(60); setTimezone(LOCAL_TIMEZONE);
    setTzSearch(""); setTzOpen(false); setError("");
    setScheduleWarning(null);
    setWeekdays([]); setEndsBy("count"); setCount(8); setUntilDate(undefined);
    setBatchTotal(0); setBatchDone(0); setBatchFailed(0); setBatchRunning(false);
  }

  function handleCancel() {
    if (batchRunning) return;
    reset();
    onDone();
  }

  function toggleWeekday(d: number) {
    setWeekdays((prev) => prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]);
    setError("");
  }

  const previewDates = mode === "recurring"
    ? buildRecurringDates(startDateLocal, weekdays, endsBy, count, untilDateStr)
    : [];

  async function handleSubmit() {
    setError(""); setScheduleWarning(null);
    if (!pickedDate) { setError("Please pick a date."); return; }

    if (mode === "single") {
      const utcStart = zonedToUtcISO(startDateLocal, timezone);
      createSession.mutate(
        { offeringId, scheduledStart: utcStart, durationMinutes: duration },
        {
          onSuccess: (res) => {
            toast({ title: "Session scheduled." });
            if (res.scheduleWarning) setScheduleWarning(res.scheduleWarning);
            else { reset(); onDone(); }
          },
          onError: () => setError("Failed to create session. Please try again."),
        },
      );
      return;
    }

    if (weekdays.length === 0) { setError("Select at least one weekday."); return; }
    if (previewDates.length === 0) { setError("No sessions generated — check your settings."); return; }
    if (endsBy === "date" && !untilDate) { setError("Please set an end date."); return; }

    setBatchRunning(true);
    setBatchTotal(previewDates.length);
    setBatchDone(0); setBatchFailed(0);

    let failed = 0;
    for (const iso of previewDates) {
      try {
        const utcIso = zonedToUtcISO(iso, timezone);
        await createSession.mutateAsync({ offeringId, scheduledStart: utcIso, durationMinutes: duration });
        setBatchDone((n) => n + 1);
      } catch {
        failed++;
        setBatchFailed((n) => n + 1);
      }
    }
    setBatchRunning(false);
    if (failed === 0) {
      toast({ title: `${previewDates.length} sessions scheduled.` });
      reset(); onDone();
    } else {
      toast({ title: `${previewDates.length - failed} scheduled, ${failed} failed.`, variant: "destructive" });
    }
  }

  const isPending = createSession.isPending || batchRunning;

  const tzOffsetLabel = (() => {
    try {
      const parts: Record<string, string> = {};
      new Intl.DateTimeFormat("en-US", { timeZone: timezone, timeZoneName: "shortOffset" })
        .formatToParts(new Date()).forEach(({ type, value }) => { parts[type] = value; });
      return parts.timeZoneName ?? "";
    } catch { return ""; }
  })();

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-5">

      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
          <CalendarPlus className="w-4 h-4 text-indigo-600" aria-hidden />
          Schedule Sessions
        </h3>
        <button
          type="button"
          onClick={handleCancel}
          disabled={batchRunning}
          className="text-slate-400 hover:text-slate-600 transition-colors duration-150 cursor-pointer"
          aria-label="Close scheduler"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Mode toggle — matches the pill tabs style used in the page header */}
      <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
        {(["single", "recurring"] as const).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => { setMode(m); setError(""); }}
            className={cn(
              "flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-sm font-medium transition-colors duration-150 cursor-pointer",
              mode === m
                ? "bg-white text-indigo-700 shadow-sm"
                : "text-slate-500 hover:text-slate-700",
            )}
          >
            {m === "single"
              ? <><CalendarPlus className="w-3.5 h-3.5" aria-hidden /> One-time</>
              : <><RefreshCw   className="w-3.5 h-3.5" aria-hidden /> Recurring</>
            }
          </button>
        ))}
      </div>

      {/* Date, time & timezone */}
      <div className="space-y-1.5">
        <Label className="text-sm font-medium text-slate-700">
          {mode === "single" ? "Date & time" : "Start date & time"}
        </Label>

        <div className="flex items-stretch gap-2">

          {/* ── Calendar date picker ── */}
          <Popover open={calOpen} onOpenChange={setCalOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className={cn(
                  "flex h-9 w-44 items-center gap-2 rounded-lg border bg-white px-3 text-sm shadow-sm transition-colors duration-150 cursor-pointer",
                  calOpen ? "border-indigo-500 ring-2 ring-indigo-500/20" : "border-slate-200 hover:border-indigo-300",
                  !pickedDate && "text-slate-400",
                )}
              >
                <CalendarPlus className="w-3.5 h-3.5 text-slate-400 shrink-0" aria-hidden />
                <span className="flex-1 text-left truncate">
                  {pickedDate
                    ? pickedDate.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric", year: "numeric" })
                    : "Pick a date"
                  }
                </span>
                <ChevronDown className="w-3.5 h-3.5 text-slate-300 shrink-0" aria-hidden />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 shadow-lg" align="start">
              <Calendar
                mode="single"
                selected={pickedDate}
                onSelect={(d) => { setPickedDate(d); setCalOpen(false); setError(""); }}
                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                initialFocus
              />
            </PopoverContent>
          </Popover>

          {/* ── Time picker: hour + minute selects ── */}
          <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 shadow-sm h-9 shrink-0">
            <Clock className="w-3.5 h-3.5 text-slate-400 shrink-0" aria-hidden />
            <select
              value={pickedHour}
              onChange={(e) => { setPickedHour(e.target.value); setError(""); }}
              className="h-full bg-transparent text-sm text-slate-800 focus:outline-none cursor-pointer appearance-none w-8 text-center"
              aria-label="Hour"
            >
              {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0")).map((h) => (
                <option key={h} value={h}>{h}</option>
              ))}
            </select>
            <span className="text-slate-400 font-bold text-xs select-none">:</span>
            <select
              value={pickedMin}
              onChange={(e) => { setPickedMin(e.target.value); setError(""); }}
              className="h-full bg-transparent text-sm text-slate-800 focus:outline-none cursor-pointer appearance-none w-8 text-center"
              aria-label="Minute"
            >
              {["00", "15", "30", "45"].map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>

          {/* ── Timezone picker ── */}
          <div className="relative shrink-0" ref={tzRef}>
            <button
              type="button"
              onClick={() => { setTzOpen((v) => !v); setTzSearch(""); }}
              className={cn(
                "flex h-9 items-center gap-1.5 rounded-lg border bg-white px-3 text-sm shadow-sm transition-colors duration-150 cursor-pointer",
                tzOpen ? "border-indigo-500 ring-2 ring-indigo-500/20" : "border-slate-200 hover:border-indigo-300",
              )}
            >
              <Globe className="w-3.5 h-3.5 text-slate-400 shrink-0" aria-hidden />
              {tzOffsetLabel && (
                <span className="text-sm font-medium text-slate-700">{tzOffsetLabel}</span>
              )}
              <ChevronDown className="w-3.5 h-3.5 text-slate-300 shrink-0" aria-hidden />
            </button>

            {tzOpen && (
              <div className="absolute z-50 top-full right-0 mt-1 w-64 rounded-xl border border-slate-200 bg-white shadow-lg overflow-hidden">
                <div className="p-2 border-b border-slate-100">
                  <input
                    type="text"
                    value={tzSearch}
                    onChange={(e) => setTzSearch(e.target.value)}
                    placeholder="Search timezone…"
                    autoFocus
                    className="w-full h-8 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <ul className="max-h-52 overflow-y-auto py-1">
                  {filteredTz.length === 0 && (
                    <li className="px-3 py-2 text-sm text-slate-400 text-center">No results</li>
                  )}
                  {filteredTz.map((tz) => (
                    <li key={tz}>
                      <button
                        type="button"
                        onClick={() => { setTimezone(tz); setTzOpen(false); setTzSearch(""); }}
                        className={cn(
                          "w-full text-left px-3 py-1.5 text-sm cursor-pointer transition-colors duration-150",
                          tz === timezone ? "bg-indigo-50 text-indigo-700 font-semibold" : "text-slate-700 hover:bg-slate-50",
                        )}
                      >
                        {tz}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

        </div>

        {/* Friendly summary line when a date+time is chosen */}
        {pickedDate && (
          <p className="text-xs text-slate-500 pl-1">
            {pickedDate.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
            {" at "}
            {pickedHour}:{pickedMin} · {timezone}
          </p>
        )}
      </div>

      {/* Duration */}
      <div className="space-y-1.5">
        <Label className="text-sm font-medium text-slate-700">Duration</Label>
        <div className="flex flex-wrap gap-2">
          {DURATION_OPTIONS.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setDuration(m)}
              className={cn(
                "px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors duration-150 cursor-pointer",
                duration === m
                  ? "bg-indigo-600 text-white border-indigo-600"
                  : "bg-white text-slate-600 border-slate-200 hover:border-indigo-300 hover:text-indigo-600",
              )}
            >
              {durationLabel(m)}
            </button>
          ))}
        </div>
      </div>

      {/* Recurring-only fields */}
      {mode === "recurring" && (
        <>
          {/* Weekday picker */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-slate-700">Repeat on</Label>
            <div className="flex gap-1.5 flex-wrap">
              {WEEKDAYS.map((label, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => toggleWeekday(idx)}
                  className={cn(
                    "w-10 h-10 rounded-lg text-xs font-semibold border transition-colors duration-150 cursor-pointer",
                    weekdays.includes(idx)
                      ? "bg-indigo-600 text-white border-indigo-600"
                      : "bg-white text-slate-600 border-slate-200 hover:border-indigo-300 hover:text-indigo-600",
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Ends */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-slate-700">Ends</Label>
            <div className="flex items-center gap-4 flex-wrap">
              {RECUR_ENDS.map((opt) => (
                <label key={opt} className="flex items-center gap-2 cursor-pointer text-sm text-slate-600">
                  <input
                    type="radio"
                    name="recur-ends"
                    value={opt}
                    checked={endsBy === opt}
                    onChange={() => setEndsBy(opt)}
                    className="accent-indigo-600"
                  />
                  {opt === "count" ? "After" : "On date"}
                </label>
              ))}
            </div>
            {endsBy === "count" ? (
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={1}
                  max={52}
                  value={count}
                  onChange={(e) => setCount(Math.max(1, Math.min(52, Number(e.target.value))))}
                  className="w-20 h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                />
                <span className="text-sm text-slate-500">sessions</span>
              </div>
            ) : (
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className={cn(
                      "flex h-9 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm shadow-sm hover:border-indigo-300 transition-colors duration-150 cursor-pointer",
                      !untilDate && "text-slate-400",
                    )}
                  >
                    <CalendarPlus className="w-3.5 h-3.5 text-slate-400 shrink-0" aria-hidden />
                    {untilDate
                      ? untilDate.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })
                      : "Pick end date"
                    }
                    <ChevronDown className="w-3.5 h-3.5 text-slate-300 shrink-0" aria-hidden />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 shadow-lg" align="start">
                  <Calendar
                    mode="single"
                    selected={untilDate}
                    onSelect={(d) => { setUntilDate(d); setError(""); }}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            )}
          </div>

          {/* Preview */}
          {previewDates.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide px-1">
                  Sessions to be created
                </p>
                <Badge className="bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-50 text-xs">
                  {previewDates.length} sessions
                </Badge>
              </div>
              <div className="rounded-xl border border-slate-200 overflow-hidden max-h-48 overflow-y-auto divide-y divide-slate-100">
                {previewDates.map((iso, i) => {
                  const d = new Date(iso);
                  const { weekday, date, time } = formatSessionDate(iso);
                  return (
                    <div key={i} className="flex items-center gap-3 bg-white px-4 py-2.5">
                      <div className="w-9 h-9 rounded-lg bg-indigo-50 flex flex-col items-center justify-center shrink-0">
                        <span className="text-xs font-bold text-indigo-400 uppercase leading-none">{weekday}</span>
                        <span className="text-sm font-bold text-indigo-700 leading-tight">{d.getDate()}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-800">{date}</p>
                        <p className="text-xs text-slate-500">{time} · {durationLabel(duration)}</p>
                      </div>
                      <span className="text-xs text-slate-300 shrink-0">#{i + 1}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Batch progress */}
          {batchRunning && (
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 space-y-1.5">
              <div className="flex items-center justify-between text-xs font-medium text-indigo-700">
                <span className="flex items-center gap-1.5">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden />
                  Scheduling… {batchDone + batchFailed} / {batchTotal}
                </span>
                {batchFailed > 0 && <span className="text-red-600">{batchFailed} failed</span>}
              </div>
              <div className="h-1.5 bg-indigo-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-indigo-600 rounded-full transition-all duration-300"
                  style={{ width: `${Math.round(((batchDone + batchFailed) / batchTotal) * 100)}%` }}
                />
              </div>
            </div>
          )}
        </>
      )}

      {/* Error */}
      {error && <p role="alert" className="text-sm text-red-600 font-medium">{error}</p>}
      <ScheduleWarningBanner warning={scheduleWarning} />

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 pt-1 border-t border-slate-100">
        <Button
          type="button"
          variant="outline"
          onClick={handleCancel}
          disabled={isPending}
          className="cursor-pointer"
        >
          Cancel
        </Button>
        <Button
          type="button"
          disabled={isPending}
          className="cursor-pointer bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5 transition-colors duration-150"
          onClick={() => void handleSubmit()}
        >
          {isPending && <Loader2 className="w-4 h-4 animate-spin" aria-hidden />}
          {mode === "single"
            ? "Schedule session"
            : previewDates.length > 0 ? `Schedule ${previewDates.length} sessions` : "Schedule sessions"
          }
        </Button>
      </div>
    </div>
  );
}

// ── Sessions Tab ──────────────────────────────────────────────────────────────

type SessionAction = { id: string; type: 'cancel' | 'reschedule' } | null;

function SessionsTab({ offeringId, totalSessionsPlanned }: { offeringId: string; totalSessionsPlanned?: number }) {
  const navigate = useNavigate();
  const { data, isLoading } = useOfferingSessions(offeringId);
  const cancelMutation     = useCancelSession(offeringId);
  const rescheduleMutation = useRescheduleSession(offeringId);

  const [schedPanelOpen, setSchedPanelOpen] = useState(false);
  const [activeAction,   setActiveAction]   = useState<SessionAction>(null);

  // Reschedule form state
  const [rsDate,     setRsDate]     = useState<Date | undefined>(undefined);
  const [rsHour,     setRsHour]     = useState("09");
  const [rsMin,      setRsMin]      = useState("00");
  const [rsCalOpen,  setRsCalOpen]  = useState(false);
  const [rsDuration, setRsDuration] = useState(60);
  const [rsTz,       setRsTz]       = useState(LOCAL_TIMEZONE);
  const [rsTzSearch, setRsTzSearch] = useState("");
  const [rsTzOpen,   setRsTzOpen]   = useState(false);
  const [rsNotify,   setRsNotify]   = useState(true);
  const [cancelNotify, setCancelNotify] = useState(true);
  const rsTzRef = React.useRef<HTMLDivElement>(null);

  const filteredRsTz = rsTzSearch.trim()
    ? ALL_TIMEZONES.filter((tz) => tz.toLowerCase().includes(rsTzSearch.toLowerCase()))
    : ALL_TIMEZONES;

  React.useEffect(() => {
    if (!rsTzOpen) return;
    function onClick(e: MouseEvent) {
      if (rsTzRef.current && !rsTzRef.current.contains(e.target as Node)) setRsTzOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [rsTzOpen]);

  const pad = (n: string | number) => String(n).padStart(2, "0");

  function clearAction() {
    setActiveAction(null);
    setRsDate(undefined); setRsHour("09"); setRsMin("00");
    setRsCalOpen(false); setRsDuration(60);
    setRsTz(LOCAL_TIMEZONE); setRsTzSearch(""); setRsTzOpen(false);
    setRsNotify(true); setCancelNotify(true);
  }

  async function handleCancel(sessionId: string) {
    try {
      await cancelMutation.mutateAsync({ sessionId, notifyStudents: cancelNotify });
      toast({ title: cancelNotify ? "Session cancelled. Students notified." : "Session cancelled." });
      clearAction();
    } catch {
      toast({ title: "Failed to cancel session.", variant: "destructive" });
    }
  }

  async function handleReschedule(sessionId: string) {
    if (!rsDate) { toast({ title: "Please pick a new date.", variant: "destructive" }); return; }
    const dtLocal = `${rsDate.getFullYear()}-${pad(rsDate.getMonth() + 1)}-${pad(rsDate.getDate())}T${pad(rsHour)}:${pad(rsMin)}`;
    const utcStart = zonedToUtcISO(dtLocal, rsTz);
    try {
      await rescheduleMutation.mutateAsync({ sessionId, scheduledStart: utcStart, durationMinutes: rsDuration, notifyStudents: rsNotify });
      toast({ title: rsNotify ? "Session rescheduled. Students notified." : "Session rescheduled." });
      clearAction();
    } catch {
      toast({ title: "Failed to reschedule session.", variant: "destructive" });
    }
  }

  const sessions = data?.sessions ?? [];
  const now      = new Date();
  const upcoming = sessions.filter((s) => s.status !== 'CANCELLED' && new Date(s.scheduledStart) >= now);
  const past     = sessions.filter((s) => s.status !== 'CANCELLED' && new Date(s.scheduledStart) <  now).slice().reverse();
  const cancelled = sessions.filter((s) => s.status === 'CANCELLED');

  const rsTzOffsetLabel = (() => {
    try {
      const parts: Record<string, string> = {};
      new Intl.DateTimeFormat("en-US", { timeZone: rsTz, timeZoneName: "shortOffset" })
        .formatToParts(new Date()).forEach(({ type, value }) => { parts[type] = value; });
      return parts.timeZoneName ?? "";
    } catch { return ""; }
  })();

  return (
    <div className="space-y-5">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-600">
          {sessions.length > 0
            ? <>
                <span className="font-semibold text-slate-800">{upcoming.length}</span> upcoming · <span className="font-semibold text-slate-800">{past.length}</span> past
                {totalSessionsPlanned ? (
                  <span className="ml-2 text-slate-400">
                    · <span className="font-semibold text-slate-600">{past.length}</span> / {totalSessionsPlanned} done
                  </span>
                ) : null}
              </>
            : <span className="text-slate-400">No sessions scheduled yet</span>
          }
        </div>
        {!schedPanelOpen && (
          <Button
            size="sm"
            className="cursor-pointer bg-indigo-600 hover:bg-indigo-700 text-white gap-2 transition-colors duration-150"
            onClick={() => setSchedPanelOpen(true)}
          >
            <CalendarPlus className="w-4 h-4" aria-hidden="true" />
            Schedule sessions
          </Button>
        )}
      </div>

      {schedPanelOpen && (
        <ScheduleSessionPanel
          offeringId={offeringId}
          onDone={() => setSchedPanelOpen(false)}
        />
      )}

      {/* Sessions list */}
      {isLoading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-16 bg-white rounded-xl border border-slate-200 animate-pulse" />
          ))}
        </div>
      ) : sessions.length === 0 ? (
        <div className="flex flex-col items-center justify-center text-center py-16 bg-white rounded-2xl border border-slate-200">
          <div className="w-14 h-14 rounded-2xl bg-indigo-50 flex items-center justify-center mb-4">
            <CalendarPlus className="w-7 h-7 text-indigo-400" aria-hidden="true" />
          </div>
          <h3 className="text-sm font-semibold text-slate-800 mb-1">No sessions yet</h3>
          <p className="text-xs text-slate-500 max-w-xs mb-4">
            Schedule a single session or set up a recurring weekly pattern.
          </p>
          <Button size="sm" className="cursor-pointer bg-indigo-600 hover:bg-indigo-700 text-white gap-2" onClick={() => setSchedPanelOpen(true)}>
            <CalendarPlus className="w-4 h-4" aria-hidden="true" />
            Schedule first session
          </Button>
        </div>
      ) : (
        <div className="space-y-5">

          {/* ── Upcoming ── */}
          {upcoming.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide px-1">
                Upcoming ({upcoming.length})
              </p>
              {upcoming.map((session) => {
                const { weekday, date, time, dayNum } = formatSessionDate(session.scheduledStart);
                const isActive = activeAction?.id === session.id;
                return (
                  <div key={session.id} className="space-y-0">
                    {/* Session card */}
                    <div className={cn(
                      "flex items-center gap-4 bg-white border border-slate-200 rounded-xl p-4 transition-all duration-150",
                      isActive ? "border-indigo-200 rounded-b-none" : "hover:border-slate-300 hover:shadow-sm",
                    )}>
                      {/* Calendar tile */}
                      <div
                        className="w-10 h-10 rounded-lg bg-indigo-50 flex flex-col items-center justify-center shrink-0 cursor-pointer"
                        onClick={() => navigate(`/teacher-classes/${offeringId}/sessions/${session.id}`)}
                      >
                        <span className="text-xs font-bold text-indigo-400 uppercase leading-none">{weekday}</span>
                        <span className="text-sm font-bold text-indigo-700 leading-tight">{dayNum}</span>
                      </div>

                      {/* Info */}
                      <div
                        className="flex-1 min-w-0 cursor-pointer"
                        onClick={() => navigate(`/teacher-classes/${offeringId}/sessions/${session.id}`)}
                      >
                        <p className="text-sm font-semibold text-slate-800">{date}</p>
                        <p className="text-xs text-slate-500">{time} · {durationLabel(session.durationMinutes)}</p>
                      </div>

                      {/* Action buttons */}
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="cursor-pointer h-8 px-2.5 text-xs text-slate-500 hover:text-indigo-700 hover:bg-indigo-50 gap-1 transition-colors duration-150"
                          onClick={() => navigate(`/teacher-classes/${offeringId}/sessions/${session.id}`)}
                        >
                          <ChevronRight className="w-3.5 h-3.5" aria-hidden /> View
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className={cn(
                            "cursor-pointer h-8 px-2.5 text-xs gap-1 transition-colors duration-150",
                            isActive && activeAction?.type === 'reschedule'
                              ? "bg-indigo-50 text-indigo-700"
                              : "text-slate-500 hover:text-indigo-700 hover:bg-indigo-50",
                          )}
                          onClick={() => {
                            if (isActive && activeAction?.type === 'reschedule') { clearAction(); return; }
                            clearAction();
                            setActiveAction({ id: session.id, type: 'reschedule' });
                          }}
                        >
                          <RefreshCw className="w-3.5 h-3.5" aria-hidden /> Reschedule
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className={cn(
                            "cursor-pointer h-8 px-2.5 text-xs gap-1 transition-colors duration-150",
                            isActive && activeAction?.type === 'cancel'
                              ? "bg-red-50 text-red-600"
                              : "text-slate-500 hover:text-red-600 hover:bg-red-50",
                          )}
                          onClick={() => {
                            if (isActive && activeAction?.type === 'cancel') { clearAction(); return; }
                            clearAction();
                            setActiveAction({ id: session.id, type: 'cancel' });
                          }}
                        >
                          <X className="w-3.5 h-3.5" aria-hidden /> Cancel
                        </Button>
                      </div>
                    </div>

                    {/* ── Inline: Cancel confirmation ── */}
                    {isActive && activeAction?.type === 'cancel' && (
                      <div className="border border-t-0 border-red-200 bg-red-50 rounded-b-xl px-4 py-4 space-y-3">
                        <div className="flex items-start gap-2.5">
                          <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" aria-hidden />
                          <div>
                            <p className="text-sm font-semibold text-red-800">Cancel this session?</p>
                            <p className="text-xs text-red-600 mt-0.5">{date} at {time}</p>
                          </div>
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={cancelNotify}
                            onChange={(e) => setCancelNotify(e.target.checked)}
                            className="accent-indigo-600 w-4 h-4"
                          />
                          <span className="text-sm text-slate-700">Notify enrolled students by email</span>
                        </label>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={clearAction}
                            disabled={cancelMutation.isPending}
                            className="cursor-pointer"
                          >
                            Keep session
                          </Button>
                          <Button
                            size="sm"
                            className="cursor-pointer bg-red-600 hover:bg-red-700 text-white gap-1.5"
                            disabled={cancelMutation.isPending}
                            onClick={() => void handleCancel(session.id)}
                          >
                            {cancelMutation.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                            Yes, cancel session
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* ── Inline: Reschedule form ── */}
                    {isActive && activeAction?.type === 'reschedule' && (
                      <div className="border border-t-0 border-indigo-200 bg-indigo-50/40 rounded-b-xl px-4 py-4 space-y-4">
                        <p className="text-sm font-semibold text-slate-800">Reschedule session</p>

                        {/* Date + time + timezone on one row */}
                        <div className="space-y-1.5">
                          <Label className="text-sm font-medium text-slate-700">New date & time</Label>
                          <div className="flex items-center gap-2 flex-wrap">

                            {/* Date picker */}
                            <Popover open={rsCalOpen} onOpenChange={setRsCalOpen}>
                              <PopoverTrigger asChild>
                                <button
                                  type="button"
                                  className={cn(
                                    "flex h-9 items-center gap-2 rounded-lg border bg-white px-3 text-sm shadow-sm transition-colors duration-150 cursor-pointer",
                                    rsCalOpen ? "border-indigo-500 ring-2 ring-indigo-500/20" : "border-slate-200 hover:border-indigo-300",
                                    !rsDate && "text-slate-400",
                                  )}
                                >
                                  <CalendarPlus className="w-3.5 h-3.5 text-slate-400 shrink-0" aria-hidden />
                                  {rsDate
                                    ? rsDate.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric", year: "numeric" })
                                    : "Pick a date"
                                  }
                                  <ChevronDown className="w-3.5 h-3.5 text-slate-300 shrink-0" aria-hidden />
                                </button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0 shadow-lg" align="start">
                                <Calendar
                                  mode="single"
                                  selected={rsDate}
                                  onSelect={(d) => { setRsDate(d); setRsCalOpen(false); }}
                                  disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                                  initialFocus
                                />
                              </PopoverContent>
                            </Popover>

                            {/* Time */}
                            <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 shadow-sm h-9 shrink-0">
                              <Clock className="w-3.5 h-3.5 text-slate-400 shrink-0" aria-hidden />
                              <select
                                value={rsHour}
                                onChange={(e) => setRsHour(e.target.value)}
                                className="h-full bg-transparent text-sm text-slate-800 focus:outline-none cursor-pointer appearance-none w-8 text-center"
                                aria-label="Hour"
                              >
                                {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0")).map((h) => (
                                  <option key={h} value={h}>{h}</option>
                                ))}
                              </select>
                              <span className="text-slate-400 font-bold text-xs select-none">:</span>
                              <select
                                value={rsMin}
                                onChange={(e) => setRsMin(e.target.value)}
                                className="h-full bg-transparent text-sm text-slate-800 focus:outline-none cursor-pointer appearance-none w-8 text-center"
                                aria-label="Minute"
                              >
                                {["00", "15", "30", "45"].map((m) => (
                                  <option key={m} value={m}>{m}</option>
                                ))}
                              </select>
                            </div>

                            {/* Timezone */}
                            <div className="relative shrink-0" ref={rsTzRef}>
                              <button
                                type="button"
                                onClick={() => { setRsTzOpen((v) => !v); setRsTzSearch(""); }}
                                className={cn(
                                  "flex h-9 items-center gap-1.5 rounded-lg border bg-white px-3 text-sm shadow-sm transition-colors duration-150 cursor-pointer",
                                  rsTzOpen ? "border-indigo-500 ring-2 ring-indigo-500/20" : "border-slate-200 hover:border-indigo-300",
                                )}
                              >
                                <Globe className="w-3.5 h-3.5 text-slate-400 shrink-0" aria-hidden />
                                {rsTzOffsetLabel && <span className="text-sm font-medium text-slate-700">{rsTzOffsetLabel}</span>}
                                <ChevronDown className="w-3.5 h-3.5 text-slate-300 shrink-0" aria-hidden />
                              </button>
                              {rsTzOpen && (
                                <div className="absolute z-50 top-full right-0 mt-1 w-64 rounded-xl border border-slate-200 bg-white shadow-lg overflow-hidden">
                                  <div className="p-2 border-b border-slate-100">
                                    <input
                                      type="text"
                                      value={rsTzSearch}
                                      onChange={(e) => setRsTzSearch(e.target.value)}
                                      placeholder="Search timezone…"
                                      autoFocus
                                      className="w-full h-8 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                  </div>
                                  <ul className="max-h-48 overflow-y-auto py-1">
                                    {filteredRsTz.length === 0 && (
                                      <li className="px-3 py-2 text-sm text-slate-400 text-center">No results</li>
                                    )}
                                    {filteredRsTz.map((tz) => (
                                      <li key={tz}>
                                        <button
                                          type="button"
                                          onClick={() => { setRsTz(tz); setRsTzOpen(false); setRsTzSearch(""); }}
                                          className={cn(
                                            "w-full text-left px-3 py-1.5 text-sm cursor-pointer transition-colors duration-150",
                                            tz === rsTz ? "bg-indigo-50 text-indigo-700 font-semibold" : "text-slate-700 hover:bg-slate-50",
                                          )}
                                        >
                                          {tz}
                                        </button>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>

                          </div>
                          {rsDate && (
                            <p className="text-xs text-slate-500 pl-1">
                              {rsDate.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric", year: "numeric" })} at {rsHour}:{rsMin} · {rsTz}
                            </p>
                          )}
                        </div>

                        {/* Duration */}
                        <div className="space-y-1.5">
                          <Label className="text-sm font-medium text-slate-700">Duration</Label>
                          <div className="flex flex-wrap gap-2">
                            {DURATION_OPTIONS.map((m) => (
                              <button
                                key={m}
                                type="button"
                                onClick={() => setRsDuration(m)}
                                className={cn(
                                  "px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors duration-150 cursor-pointer",
                                  rsDuration === m
                                    ? "bg-indigo-600 text-white border-indigo-600"
                                    : "bg-white text-slate-600 border-slate-200 hover:border-indigo-300 hover:text-indigo-600",
                                )}
                              >
                                {durationLabel(m)}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Notify */}
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={rsNotify}
                            onChange={(e) => setRsNotify(e.target.checked)}
                            className="accent-indigo-600 w-4 h-4"
                          />
                          <span className="text-sm text-slate-700">Notify enrolled students about the new time</span>
                        </label>

                        <div className="flex gap-2 pt-1 border-t border-indigo-100">
                          <Button size="sm" variant="outline" onClick={clearAction} disabled={rescheduleMutation.isPending} className="cursor-pointer">
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            className="cursor-pointer bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5"
                            disabled={rescheduleMutation.isPending}
                            onClick={() => void handleReschedule(session.id)}
                          >
                            {rescheduleMutation.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                            Confirm reschedule
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Past sessions ── */}
          {past.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide px-1">
                Past sessions ({past.length})
              </p>
              {past.map((session) => {
                const { weekday, date, time, dayNum } = formatSessionDate(session.scheduledStart);
                const summarySent = !!session.summarySentAt;
                return (
                  <div
                    key={session.id}
                    className="flex items-center gap-4 bg-white border border-slate-200 rounded-xl p-4 hover:border-slate-300 hover:shadow-sm transition-all duration-150 cursor-pointer group"
                    onClick={() => navigate(`/teacher-classes/${offeringId}/sessions/${session.id}`)}
                  >
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex flex-col items-center justify-center shrink-0">
                      <span className="text-xs font-bold text-slate-400 uppercase leading-none">{weekday}</span>
                      <span className="text-sm font-bold text-slate-600 leading-tight">{dayNum}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-700">{date}</p>
                      <p className="text-xs text-slate-400">{time} · {durationLabel(session.durationMinutes)}</p>
                    </div>
                    {summarySent ? (
                      <Badge className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-50 shrink-0">
                        <CheckCircle2 className="w-3 h-3 mr-1" aria-hidden="true" /> Summary sent
                      </Badge>
                    ) : (
                      <Badge className="text-xs bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-50 shrink-0">
                        No summary
                      </Badge>
                    )}
                    <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 transition-colors duration-150 shrink-0" aria-hidden="true" />
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Cancelled sessions ── */}
          {cancelled.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide px-1">
                Cancelled ({cancelled.length})
              </p>
              {cancelled.map((session) => {
                const { weekday, date, time, dayNum } = formatSessionDate(session.scheduledStart);
                return (
                  <div
                    key={session.id}
                    className="flex items-center gap-4 bg-slate-50 border border-slate-200 rounded-xl p-4 opacity-60"
                  >
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex flex-col items-center justify-center shrink-0">
                      <span className="text-xs font-bold text-slate-400 uppercase leading-none line-through">{weekday}</span>
                      <span className="text-sm font-bold text-slate-400 leading-tight">{dayNum}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-500 line-through">{date}</p>
                      <p className="text-xs text-slate-400">{time} · {durationLabel(session.durationMinutes)}</p>
                    </div>
                    <Badge className="text-xs bg-slate-100 text-slate-500 border border-slate-200 hover:bg-slate-100 shrink-0">
                      Cancelled
                    </Badge>
                  </div>
                );
              })}
            </div>
          )}

        </div>
      )}
    </div>
  );
}

// ── Broadcast Tab ─────────────────────────────────────────────────────────────

function formatExpiry(iso: string | null): string {
  if (!iso) return "No expiry";
  const d = new Date(iso);
  const now = new Date();
  if (d < now) return "Expired";
  const diffDays = Math.ceil((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 1) return "Expires tomorrow";
  if (diffDays < 7) return `Expires in ${diffDays} days`;
  return `Expires ${d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;
}

function BroadcastTab({ offeringId, confirmedCount }: { offeringId: string; confirmedCount: number }) {
  const sendBroadcast = useSendBroadcast();
  const editBroadcastMutation = useEditBroadcast();
  const deleteBroadcastMutation = useDeleteBroadcast();
  const { data: broadcasts, isLoading: broadcastsLoading } = useTeacherBroadcasts(offeringId);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editSubject, setEditSubject] = useState("");
  const [editBody, setEditBody] = useState("");
  const [editExpiry, setEditExpiry] = useState("");

  function startEdit(b: { id: string; subject: string; body: string; expiresAt: string | null }) {
    setEditingId(b.id);
    setEditSubject(b.subject);
    setEditBody(b.body);
    setEditExpiry(b.expiresAt ? b.expiresAt.slice(0, 10) : "");
  }

  async function handleSend(subject: string, body: string, expiresAt?: string) {
    await sendBroadcast.mutateAsync({ offeringId, subject, body, expiresAt });
    toast({ title: `Message sent to ${confirmedCount} student${confirmedCount !== 1 ? "s" : ""}.` });
  }

  async function handleSaveEdit() {
    if (!editingId) return;
    await editBroadcastMutation.mutateAsync({
      broadcastId: editingId,
      offeringId,
      subject: editSubject,
      body: editBody,
      expiresAt: editExpiry ? new Date(editExpiry).toISOString() : undefined,
    });
    toast({ title: "Broadcast updated." });
    setEditingId(null);
  }

  async function handleDelete(broadcastId: string) {
    await deleteBroadcastMutation.mutateAsync({ broadcastId, offeringId });
    toast({ title: "Broadcast deleted." });
  }

  const now = new Date();

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-sm text-slate-600">
        <Mail className="w-4 h-4 text-indigo-600" aria-hidden="true" />
        {confirmedCount > 0
          ? <>Sending to <span className="font-semibold text-slate-900 mx-1">{confirmedCount}</span> confirmed {confirmedCount === 1 ? "student" : "students"}.</>
          : <span className="text-slate-400">No confirmed students to message yet.</span>
        }
      </div>

      <BroadcastComposer
        offeringId={offeringId}
        confirmedCount={confirmedCount}
        broadcastsLast24h={0}
        onSend={handleSend}
        showExpiry
      />

      {/* Sent broadcasts list */}
      <div className="space-y-3">
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Sent messages</p>

        {broadcastsLoading && (
          <div className="space-y-2">
            {[0, 1].map((i) => <div key={i} className="h-20 bg-white rounded-xl border border-slate-200 animate-pulse" />)}
          </div>
        )}

        {!broadcastsLoading && (broadcasts ?? []).length === 0 && (
          <p className="text-sm text-slate-400">No messages sent yet.</p>
        )}

        {(broadcasts ?? []).map((b) => {
          const expired = b.expiresAt != null && new Date(b.expiresAt) < now;
          const isEditing = editingId === b.id;

          if (isEditing) {
            return (
              <div key={b.id} className="bg-white border border-indigo-200 rounded-xl p-4 space-y-3">
                <input
                  className="w-full text-sm font-medium border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300"
                  value={editSubject}
                  onChange={(e) => setEditSubject(e.target.value)}
                  placeholder="Subject"
                />
                <textarea
                  className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 resize-none"
                  rows={3}
                  value={editBody}
                  onChange={(e) => setEditBody(e.target.value)}
                  placeholder="Message body"
                />
                <div className="flex items-center gap-2">
                  <label className="text-xs text-slate-500 shrink-0">Expires on</label>
                  <input
                    type="date"
                    className="text-sm border border-slate-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-300"
                    value={editExpiry}
                    onChange={(e) => setEditExpiry(e.target.value)}
                  />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleSaveEdit} disabled={editBroadcastMutation.isPending}>
                    Save
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>Cancel</Button>
                </div>
              </div>
            );
          }

          return (
            <div
              key={b.id}
              className={cn(
                "bg-white border rounded-xl p-4 space-y-1",
                expired ? "border-slate-200 opacity-60" : "border-slate-200",
              )}
            >
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-semibold text-slate-900">{b.subject}</p>
                <div className="flex items-center gap-1 shrink-0">
                  {!expired && (
                    <>
                      <button
                        onClick={() => startEdit(b)}
                        className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-indigo-600 transition-colors cursor-pointer"
                        title="Edit"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDelete(b.id)}
                        className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-red-500 transition-colors cursor-pointer"
                        title="Delete"
                        disabled={deleteBroadcastMutation.isPending}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">{b.body}</p>
              <div className="flex items-center gap-3 pt-1 flex-wrap">
                <span className="text-xs text-slate-400">
                  Sent {new Date(b.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                  {" · "}{b.sentCount} delivered
                </span>
                <span className={cn(
                  "text-xs font-medium",
                  expired ? "text-red-400" : "text-slate-400",
                )}>
                  {formatExpiry(b.expiresAt)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Curriculum tab ─────────────────────────────────────────────────────────────

function CurriculumTab({ offering, sessionsDone }: { offering: OfferingResponse; sessionsDone: number }) {
  const milestones =
    offering.group?.curriculumMilestones ??
    offering.group?.milestones ??
    (offering.crashCourse as any)?.milestones ??
    [];

  const sessionsPerWeek =
    offering.group?.sessionsPerWeek ??
    offering.crashCourse?.sessionsPerWeek ??
    1;

  // Estimate current week from sessions done
  const currentWeek = Math.floor(sessionsDone / sessionsPerWeek) + 1;

  if (!milestones.length) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-16 bg-white rounded-2xl border border-slate-200">
        <div className="w-14 h-14 rounded-2xl bg-indigo-50 flex items-center justify-center mb-4">
          <BookOpen className="w-7 h-7 text-indigo-400" aria-hidden="true" />
        </div>
        <h3 className="text-sm font-semibold text-slate-800 mb-1">No curriculum plan yet</h3>
        <p className="text-xs text-slate-500 max-w-xs">
          Add a week-by-week curriculum to your offering to track progress here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">
        Estimated progress based on sessions completed. Week {Math.min(currentWeek, milestones.length)} of {milestones.length}.
      </p>
      {milestones.map((m: any, i: number) => {
        const weekNum = m.weekNumber ?? i + 1;
        const isDone = weekNum < currentWeek;
        const isCurrent = weekNum === currentWeek;
        const badge = m.assessmentType && m.assessmentType !== 'NONE' ? ASSESSMENT_BADGE[m.assessmentType as keyof typeof ASSESSMENT_BADGE] : null;
        return (
          <div
            key={i}
            className={cn(
              "flex items-start gap-3 p-4 rounded-xl border transition-colors",
              isDone    ? "bg-emerald-50 border-emerald-200" :
              isCurrent ? "bg-indigo-50 border-indigo-300 shadow-sm" :
                          "bg-white border-slate-200",
            )}
          >
            {/* Week indicator */}
            <div className={cn(
              "w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold shrink-0",
              isDone    ? "bg-emerald-500 text-white" :
              isCurrent ? "bg-indigo-600 text-white" :
                          "bg-slate-100 text-slate-500",
            )}>
              {isDone ? <CheckCircle2 className="w-4 h-4" /> : weekNum}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className={cn(
                  "text-sm font-semibold",
                  isDone ? "text-emerald-800" : isCurrent ? "text-indigo-900" : "text-slate-700",
                )}>
                  {m.title || `Week ${weekNum}`}
                </p>
                {isCurrent && (
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-indigo-600 text-white">
                    Current
                  </span>
                )}
                {badge && (
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                    {badge}
                  </span>
                )}
              </div>
              {m.description && (
                <p className="text-xs text-slate-500 mt-0.5">{m.description}</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function OfferingClassManagementPage() {
  const { offeringId = "" } = useParams<{ offeringId: string }>();

  const { teacherProfile } = useTeacherProfile();
  const teacherName     = teacherProfile?.fullName     || "";
  const teacherAvatar   = teacherProfile?.profileImage || "";
  const teacherSubjects: string[] = (teacherProfile?.subjects || [])
    .map((s: any) => typeof s === "string" ? s : s?.subjectName || s?.subject?.name || s?.name || "")
    .filter(Boolean);

  // Resolve offering title from the hub cache — no extra API call needed
  const hubQuery      = useClassManagementHub();
  const offeringTitle = hubQuery.data?.find((o) => o.offeringId === offeringId)?.title ?? "";

  // Full offering data (for curriculum, duration, mode, etc.)
  const { data: allOfferings } = useMyOfferings();
  const fullOffering: OfferingResponse | undefined = allOfferings?.find((o) => o.id === offeringId);

  const enrollmentsQuery = useOfferingEnrollments(offeringId);
  const sessionsQuery    = useOfferingSessions(offeringId);

  const confirmedCount = enrollmentsQuery.data?.filter((e) => e.status === "CONFIRMED").length ?? 0;
  const pendingCount   = enrollmentsQuery.data?.filter((e) => e.status === "PENDING").length   ?? 0;
  const sessionCount   = sessionsQuery.data?.sessions?.length ?? 0;

  // Offering-level metadata
  const offeringType       = fullOffering?.offeringType ?? hubQuery.data?.find((o) => o.offeringId === offeringId)?.offeringType;
  const sessionDuration    = fullOffering?.oneToOne?.sessionDurationMinutes ?? fullOffering?.group?.sessionDurationMinutes ?? fullOffering?.crashCourse?.sessionDurationMinutes;
  const sessionsPerWeek    = fullOffering?.group?.sessionsPerWeek ?? fullOffering?.crashCourse?.sessionsPerWeek;
  const totalSessionsPlanned = fullOffering?.group?.totalSessionsPlanned ?? fullOffering?.crashCourse?.totalSessions;
  const teachingMode       = fullOffering?.teachingMode;
  const hasCurriculum      = offeringType === "GROUP" || offeringType === "CRASH_COURSE";

  // Sessions done = past (non-cancelled) sessions
  const now = new Date();
  const sessionsDone = (sessionsQuery.data?.sessions ?? []).filter(
    (s) => s.status !== "CANCELLED" && new Date(s.scheduledStart) < now,
  ).length;

  // Keep page title dynamic once we know the offering name
  usePageTitle(offeringTitle ? `${offeringTitle} | BookMyCoaching` : "Class Management | BookMyCoaching");

  return (
    <div className="min-h-screen bg-white teacher-theme">
      <SidebarProvider>
        <DashboardTopNav role="teacher" />
        <div className="min-h-screen flex w-full pt-14">
          <TeacherSidebar
            teacherName={teacherName}
            teacherAvatar={teacherAvatar}
            subjects={teacherSubjects}
          />
          <div className="flex-1 flex flex-col min-w-0">

            {/* Edge-to-edge page header */}
            <div className="px-4 sm:px-6 pt-5 pb-4 border-b border-slate-100 shrink-0 bg-white">
              <Link
                to="/teacher-classes"
                className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-700 transition-colors duration-150 mb-2"
              >
                <ArrowLeft className="w-3.5 h-3.5" aria-hidden="true" />
                All classes
              </Link>

              {/* Title — shows offering name, falls back while hub loads */}
              <h1 className="text-2xl font-bold text-slate-900 leading-tight">
                {offeringTitle || "Class Management"}
              </h1>

              {/* Stats row */}
              <div className="flex flex-wrap items-center gap-4 mt-1.5 text-sm text-slate-500">
                <div className="flex items-center gap-1.5">
                  <Users className="w-4 h-4" aria-hidden="true" />
                  <span className="font-semibold text-slate-700">{confirmedCount}</span> students
                  {pendingCount > 0 && (
                    <span className="ml-1 text-amber-600 font-medium">
                      · {pendingCount} pending
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  <CalendarPlus className="w-4 h-4" aria-hidden="true" />
                  <span className="font-semibold text-slate-700">{sessionCount}</span> sessions
                  {totalSessionsPlanned ? (
                    <span className="text-slate-400">/ {totalSessionsPlanned} planned</span>
                  ) : null}
                </div>
                {sessionDuration && (
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4" aria-hidden="true" />
                    <span>{sessionDuration} min</span>
                  </div>
                )}
                {sessionsPerWeek && (
                  <div className="flex items-center gap-1.5">
                    <CalendarPlus className="w-4 h-4" aria-hidden="true" />
                    <span>{sessionsPerWeek}× / week</span>
                  </div>
                )}
                {teachingMode && (
                  <div className="flex items-center gap-1.5">
                    <MapPin className="w-4 h-4" aria-hidden="true" />
                    <span className="capitalize">{teachingMode.toLowerCase().replace(/_/g, ' ')}</span>
                  </div>
                )}
              </div>
            </div>

            <main className="flex-1 overflow-auto bg-slate-50">
              <div className="px-4 sm:px-6 py-6 max-w-4xl space-y-5">
                <Tabs defaultValue="roster" className="w-full">
                  <TabsList className="bg-white border border-slate-200 rounded-xl p-1 h-auto gap-1">
                    <TabsTrigger
                      value="roster"
                      className={cn(
                        "cursor-pointer rounded-lg px-4 py-2 text-sm font-medium transition-colors duration-150",
                        "data-[state=active]:bg-indigo-600 data-[state=active]:text-white data-[state=active]:shadow-sm",
                        "data-[state=inactive]:text-slate-600 data-[state=inactive]:hover:text-slate-900",
                      )}
                    >
                      <Users className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />
                      Roster
                      {pendingCount > 0 && (
                        <span className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-amber-400 text-white text-xs font-bold">
                          {pendingCount}
                        </span>
                      )}
                    </TabsTrigger>
                    <TabsTrigger
                      value="sessions"
                      className={cn(
                        "cursor-pointer rounded-lg px-4 py-2 text-sm font-medium transition-colors duration-150",
                        "data-[state=active]:bg-indigo-600 data-[state=active]:text-white data-[state=active]:shadow-sm",
                        "data-[state=inactive]:text-slate-600 data-[state=inactive]:hover:text-slate-900",
                      )}
                    >
                      <CalendarPlus className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />
                      Sessions
                      {sessionCount > 0 && (
                        <span className="ml-1.5 text-xs font-semibold opacity-70">{sessionCount}</span>
                      )}
                    </TabsTrigger>
                    {hasCurriculum && (
                      <TabsTrigger
                        value="curriculum"
                        className={cn(
                          "cursor-pointer rounded-lg px-4 py-2 text-sm font-medium transition-colors duration-150",
                          "data-[state=active]:bg-indigo-600 data-[state=active]:text-white data-[state=active]:shadow-sm",
                          "data-[state=inactive]:text-slate-600 data-[state=inactive]:hover:text-slate-900",
                        )}
                      >
                        <BookOpen className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />
                        Curriculum
                      </TabsTrigger>
                    )}
                    <TabsTrigger
                      value="broadcast"
                      className={cn(
                        "cursor-pointer rounded-lg px-4 py-2 text-sm font-medium transition-colors duration-150",
                        "data-[state=active]:bg-indigo-600 data-[state=active]:text-white data-[state=active]:shadow-sm",
                        "data-[state=inactive]:text-slate-600 data-[state=inactive]:hover:text-slate-900",
                      )}
                    >
                      <Send className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />
                      Broadcast
                    </TabsTrigger>
                  </TabsList>

                  <div className="mt-5">
                    <TabsContent value="roster">
                      <RosterTab offeringId={offeringId} />
                    </TabsContent>
                    <TabsContent value="sessions">
                      <SessionsTab offeringId={offeringId} totalSessionsPlanned={totalSessionsPlanned} />
                    </TabsContent>
                    {hasCurriculum && fullOffering && (
                      <TabsContent value="curriculum">
                        <CurriculumTab offering={fullOffering} sessionsDone={sessionsDone} />
                      </TabsContent>
                    )}
                    <TabsContent value="broadcast">
                      <BroadcastTab offeringId={offeringId} confirmedCount={confirmedCount} />
                    </TabsContent>
                  </div>
                </Tabs>
              </div>
            </main>
          </div>
        </div>
      </SidebarProvider>
    </div>
  );
}
